import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { useUserCredits } from '@/hooks/useCredits';
import { useTranslation } from '@/hooks/useTranslation';
import { useUserStore } from '@/hooks/useUser';
import { logCreditsPurchase } from '@/utilities/ga';
import { Sparkles, Zap } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import Badge from './Badge';
import { RainbowButton } from './ui/rainbow-button';

interface CreditPurchaseModalProps {
	trigger?: React.ReactNode;
	onSuccessfulPurchase?: () => void;
}

interface CreditPackage {
	id: string;
	name: string;
	credits: number;
	price: number;
	value: string;
}

// Base credit packages - available in all environments
const BASE_CREDIT_PACKAGES: CreditPackage[] = [
	{
		id: 'small',
		name: 'Small',
		credits: 5,
		price: 2.99,
		value: '$0.60/credit'
	},
	{
		id: 'medium',
		name: 'Medium',
		credits: 15,
		price: 7.99,
		value: '$0.53/credit'
	},
	{
		id: 'large',
		name: 'Large',
		credits: 30,
		price: 13.99,
		value: '$0.47/credit'
	}
];

// Test package - only available in development
const TEST_CREDIT_PACKAGE: CreditPackage = {
	id: 'test',
	name: 'Test',
	credits: 5,
	price: 0,
	value: '$0.00/credit'
};

// Get all credit packages based on environment
const CREDIT_PACKAGES = process.env.NODE_ENV === 'development'
	? [TEST_CREDIT_PACKAGE, ...BASE_CREDIT_PACKAGES]
	: BASE_CREDIT_PACKAGES;

export default function CreditPurchaseModal({ trigger, onSuccessfulPurchase }: CreditPurchaseModalProps) {
	const [isOpen, setIsOpen] = useState(false);
	const [selectedPackage, setSelectedPackage] = useState<string | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const { user, customerDetails } = useUserStore();
	const { t } = useTranslation();

	// Fetch user's credit information
	const { data: creditInfo, isLoading: isLoadingCredits, refetch } = useUserCredits();

	const handlePurchase = async () => {
		if (!selectedPackage || !user || !customerDetails?.email) {
			toast.error('Please select a credit package');
			return;
		}

		setIsLoading(true);

		// Get the selected package details
		const selectedPkg = CREDIT_PACKAGES.find(pkg => pkg.id === selectedPackage);

		try {
			const response = await fetch('/api/credits/checkout', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({
					packageId: selectedPackage,
					email: customerDetails.email,
					userId: user.uid,
				}),
			});

			if (!response.ok) {
				const error = await response.json();
				throw new Error(error.error || 'Failed to create checkout session');
			}

			const { checkoutSessionUrl } = await response.json();

			// Track the credit purchase
			if (selectedPkg) {
				logCreditsPurchase({
					amount: selectedPkg.credits,
					purchaseId: selectedPackage,
					additionalParams: {
						price: selectedPkg.price,
						package_name: selectedPkg.name,
						user_id: user.uid
					}
				});
			}

			// Redirect to checkout
			window.location.href = checkoutSessionUrl;

			// Close the modal
			setIsOpen(false);
			setSelectedPackage(null);

			if (onSuccessfulPurchase) {
				onSuccessfulPurchase();
			}
		} catch (error) {
			console.error('Error creating checkout session:', error);
			toast.error(error instanceof Error ? error.message : 'Failed to create checkout session');
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<Dialog open={isOpen} onOpenChange={setIsOpen}>
			<DialogTrigger asChild>
				{trigger || (
					<Button variant="default" className="gap-2">
						<Zap className="h-4 w-4" />
						{t('creditPurchaseModal.buttonText', 'Buy Credits')}
					</Button>
				)}
			</DialogTrigger>

			<DialogContent className="sm:max-w-[500px]">
				<DialogHeader>
					<DialogTitle className="flex items-center gap-2">
						{t('creditPurchaseModal.title', 'Purchase AI Generation Credits')}
						<Badge>Pro</Badge>
					</DialogTitle>
					<DialogDescription>
						{t('creditPurchaseModal.description', 'Purchase additional AI generation credits to customize more cards.')}
					</DialogDescription>
				</DialogHeader>

				<div className="py-4">
					{isLoadingCredits ? (
						<div className="text-center py-8">Loading credit information...</div>
					) : (
						<>
							<div className="mb-4 flex items-center justify-between p-3 bg-secondary/30 rounded-lg">
								<div className="flex items-center gap-2">
									<Sparkles className="h-5 w-5 text-primary" />
									<span>{t('creditPurchaseModal.currentCredits', 'Current Credits')}:</span>
								</div>
								<span className="font-semibold text-lg">
									{creditInfo?.totalCredits || 0}
								</span>
							</div>

							<div className="space-y-4">
								<h3 className="text-sm font-medium">
									{t('creditPurchaseModal.selectPackage', 'Select Credit Package')}:
								</h3>

								<div className="grid gap-3">
									{CREDIT_PACKAGES.map((pkg) => (
										<button
											key={pkg.id}
											type="button"
											className={`flex items-center justify-between p-4 border rounded-lg transition-colors ${selectedPackage === pkg.id
												? 'border-primary bg-primary/10'
												: 'border-border hover:border-primary/50'
												}`}
											onClick={() => setSelectedPackage(pkg.id)}
										>
											<div className="flex flex-col items-start">
												<span className="font-medium flex items-center gap-2">{pkg.name} Pack {pkg.id === 'medium' && <Badge>Popular</Badge>}</span>
												<span className="text-sm text-muted-foreground">{pkg.credits} Credits</span>
											</div>
											<div className="flex flex-col items-end">
												<span className="font-semibold">${pkg.price}</span>
												<span className="text-xs text-muted-foreground">{pkg.value}</span>
											</div>
										</button>
									))}
								</div>

								<RainbowButton
									className="!w-full mt-4"
									disabled={!selectedPackage || isLoading}
									onClick={handlePurchase}
								>
									{isLoading ? t('common.loading', 'Loading...') : t('creditPurchaseModal.purchase', 'Purchase Now')}
								</RainbowButton>
							</div>
						</>
					)}
				</div>
			</DialogContent>
		</Dialog>
	);
} 